import { useEffect, useRef, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Palette, Upload, X, Copy } from "lucide-react";

const empty = {
  nombre: "", descripcion: "", precio: 0, precio_costo: 0, descuento: 0,
  categoria: "", imagen: "", fotos: [], variantes: [],
  opciones_color: [], opciones_estilo: [], opciones_tela: [], opciones_tamano: [],
  receta: [],
  activo: true,
  // Campos exclusivos para la tienda web (no afectan el precio interno)
  precio_web: "",           // Precio normal en la tienda web (si vacío usa `precio`)
  precio_oferta: "",        // Precio de oferta en la tienda web (opcional)
  oferta_hasta: "",
  publicar_en_tienda: false,
  es_destacado: false,
};

function OptionEditor({ label, items, onChange, testid }) {
  const add = () => onChange([...(items || []), { nombre: "", precio_extra: 0 }]);
  const upd = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const rem = (i) => onChange(items.filter((_, x) => x !== i));
  return (
    <div className="border border-zinc-200 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] uppercase tracking-widest">{label}</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`opt-${testid}-add`}>
          <Plus className="w-3 h-3" /> Agregar
        </Button>
      </div>
      {(items || []).length === 0 && <div className="text-[11px] text-zinc-400 italic">Sin opciones</div>}
      <div className="space-y-1.5">
        {(items || []).map((opt, i) => (
          <div key={i} className="grid grid-cols-12 gap-1.5">
            <Input className="col-span-7 h-8 text-sm" placeholder="Nombre (ej. Terciopelo)" value={opt.nombre} onChange={(e) => upd(i, "nombre", e.target.value)} data-testid={`opt-${testid}-name-${i}`} />
            <Input className="col-span-4 h-8 text-sm" type="number" step="0.01" placeholder="+Q0" value={opt.precio_extra} onChange={(e) => upd(i, "precio_extra", Number(e.target.value))} data-testid={`opt-${testid}-price-${i}`} />
            <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-red-600" onClick={() => rem(i)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}


function RecetaEditor({ items, inventarioOptions, onChange }) {
  const add = () => onChange([...(items || []), { inventario_producto_id: "", cantidad: 1 }]);
  const upd = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const rem = (i) => onChange((items || []).filter((_, x) => x !== i));
  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Receta / Insumos del inventario</Label>
          <div className="text-[11px] text-zinc-500 mt-0.5">Al vender este producto se descuentan estas cantidades del stock automáticamente.</div>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="receta-add">
          <Plus className="w-3 h-3" /> Insumo
        </Button>
      </div>
      {(items || []).length === 0 && <div className="text-[11px] text-zinc-400 italic">Sin receta (no descuenta stock al vender)</div>}
      <div className="space-y-1.5">
        {(items || []).map((r, i) => {
          const inv = inventarioOptions.find((x) => x.id === r.inventario_producto_id);
          return (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
              <select
                className="col-span-8 h-8 text-sm border border-zinc-300 rounded px-2 bg-white"
                value={r.inventario_producto_id || ""}
                onChange={(e) => upd(i, "inventario_producto_id", e.target.value)}
                data-testid={`receta-inv-${i}`}
              >
                <option value="">— Selecciona insumo del inventario —</option>
                {inventarioOptions.map((it) => (
                  <option key={it.id} value={it.id}>{it.nombre}{it.tamano ? ` · ${it.tamano}` : ""}{it.color ? ` · ${it.color}` : ""} (stock: {it.stock_actual} {it.unidad || ""})</option>
                ))}
              </select>
              <Input className="col-span-3 h-8 text-sm" type="number" step="0.01" placeholder="Cantidad" value={r.cantidad} onChange={(e) => upd(i, "cantidad", Number(e.target.value))} data-testid={`receta-cant-${i}`} />
              <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-red-600" onClick={() => rem(i)}><Trash2 className="w-3 h-3" /></Button>
              {inv && <div className="col-span-12 -mt-1 text-[10px] text-blue-800">1× {inv.nombre} → {r.cantidad} {inv.unidad || ""}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}



export default function Productos() {
  const [items, setItems] = useState([]);
  const [inventarioItems, setInventarioItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [{ data }, invR] = await Promise.all([
      api.get("/products"),
      api.get("/inventario/productos").catch(() => ({ data: [] })),
    ]);
    setItems(data);
    setInventarioItems(invR.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    // Normalizamos: si el producto tenía `imagenes` (galería web) pero no `fotos`, las mostramos aquí también.
    const fotos = (p.fotos && p.fotos.length ? p.fotos : (p.imagenes || (p.imagen ? [p.imagen] : []))) || [];
    setForm({ ...empty, ...p, fotos,
      precio_web: p.precio_web ?? "",
      precio_oferta: p.precio_oferta ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        precio: Number(form.precio),
        precio_costo: Number(form.precio_costo || 0),
        descuento: Number(form.descuento),
        // Campos web opcionales: enviar null si están vacíos, número si tienen valor
        precio_web: form.precio_web === "" || form.precio_web === null || form.precio_web === undefined
          ? null
          : Number(form.precio_web),
        precio_oferta: form.precio_oferta === "" || form.precio_oferta === null || form.precio_oferta === undefined
          ? null
          : Number(form.precio_oferta),
        // Sincronizar imagen principal + galería
        imagenes: form.fotos || [],
        imagen: (form.fotos || [])[0] || form.imagen || "",
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.id}/duplicate`);
      toast.success(`Duplicado como "${data.nombre}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFiles = async (evt) => {
    const files = Array.from(evt.target.files || []);
    if (files.length === 0) return;
    const results = [];
    for (const f of files) {
      if (f.size > 800 * 1024) {
        toast.error(`${f.name}: máx 800KB`);
        continue;
      }
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(f);
      });
      results.push(b64);
    }
    setForm((f) => ({ ...f, fotos: [...(f.fotos || []), ...results] }));
    evt.target.value = "";
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, fotos: f.fotos.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-6" data-testid="productos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Inventario</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">Catálogo</h1>
          <p className="text-zinc-500 mt-2 text-sm">Productos con variantes de color, estilo, tela y tamaño con precio extra.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-product-btn">
              <Plus className="w-4 h-4" /> Nuevo producto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
              <DialogDescription>Define precio base, precio costo, fotos y opciones de variantes con precio extra.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="product-nombre" />
              </div>
              <div>
                <Label>Precio venta (Q)</Label>
                <Input type="number" min={0} step="0.01" value={form.precio}
                       onChange={(e) => setForm({ ...form, precio: e.target.value })} data-testid="product-precio" />
              </div>
              <div>
                <Label>Precio costo (Q)</Label>
                <Input type="number" min={0} step="0.01" value={form.precio_costo}
                       onChange={(e) => setForm({ ...form, precio_costo: e.target.value })} data-testid="product-costo" />
              </div>
              <div>
                <Label>Descuento (%)</Label>
                <Input type="number" min={0} max={100} step="0.1" value={form.descuento}
                       onChange={(e) => setForm({ ...form, descuento: e.target.value })} data-testid="product-descuento" />
              </div>
              <div>
                <Label>Categoría</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
              </div>

              {/* Precios exclusivos para la tienda web (no afectan el precio interno del catálogo) */}
              <div className="col-span-2 border-2 border-dashed border-[#FFD100] rounded-md p-3 bg-[#FFFCE8]">
                <div className="text-[10px] uppercase tracking-widest font-black text-zinc-600 mb-2">🛒 Precios para la tienda web (independientes del catálogo)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Precio normal web (Q)</Label>
                    <Input type="number" min={0} step="0.01" placeholder={`Usa Q${Number(form.precio || 0).toFixed(2)}`}
                           value={form.precio_web} onChange={(e) => setForm({ ...form, precio_web: e.target.value })}
                           data-testid="product-precio-web" />
                    <div className="text-[10px] text-zinc-500 mt-0.5">Vacío = usa el precio del catálogo</div>
                  </div>
                  <div>
                    <Label className="text-xs">Precio oferta web (Q)</Label>
                    <Input type="number" min={0} step="0.01" placeholder="Vacío = sin oferta"
                           value={form.precio_oferta} onChange={(e) => setForm({ ...form, precio_oferta: e.target.value })}
                           data-testid="product-precio-oferta" />
                    <div className="text-[10px] text-zinc-500 mt-0.5">Se muestra tachado el precio normal</div>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </div>

              {/* Fotos */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-widest">Fotos ({(form.fotos || []).length}) — La 1ª es la principal</Label>
                  <>
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-upload-input" />
                    <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="photo-upload-btn">
                      <Upload className="w-3 h-3" /> Subir foto
                    </Button>
                  </>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(form.fotos || []).map((src, i) => (
                    <div key={i} className={`relative group aspect-square rounded-md overflow-hidden border-2 ${i === 0 ? "border-[#E30613]" : "border-zinc-200"}`}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      {i === 0 && (
                        <div className="absolute top-1 left-1 bg-[#E30613] text-white text-[8px] font-black px-1.5 py-0.5 uppercase tracking-widest">Principal</div>
                      )}
                      <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition" data-testid={`photo-remove-${i}`}>
                        <X className="w-3 h-3" />
                      </button>
                      {i !== 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...form.fotos];
                            const [chosen] = next.splice(i, 1);
                            next.unshift(chosen);
                            setForm({ ...form, fotos: next });
                          }}
                          className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[9px] font-bold py-1 opacity-0 group-hover:opacity-100 transition uppercase tracking-widest"
                          data-testid={`photo-set-main-${i}`}
                        >
                          Marcar principal
                        </button>
                      )}
                    </div>
                  ))}
                  {(form.fotos || []).length === 0 && (
                    <div className="col-span-4 text-[11px] text-zinc-400 border border-dashed rounded-md py-4 text-center">Sin fotos. Máx 800KB c/u.</div>
                  )}
                </div>
              </div>

              {/* Opciones de variantes */}
              <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <OptionEditor label="Color" items={form.opciones_color} onChange={(v) => setForm({ ...form, opciones_color: v })} testid="color" />
                <OptionEditor label="Estilo" items={form.opciones_estilo} onChange={(v) => setForm({ ...form, opciones_estilo: v })} testid="estilo" />
                <OptionEditor label="Tela" items={form.opciones_tela} onChange={(v) => setForm({ ...form, opciones_tela: v })} testid="tela" />
                <OptionEditor label="Tamaño" items={form.opciones_tamano} onChange={(v) => setForm({ ...form, opciones_tamano: v })} testid="tamano" />
              </div>

              {/* Receta (insumos del inventario que se descuentan al vender) */}
              <div className="col-span-2">
                <RecetaEditor
                  items={form.receta || []}
                  inventarioOptions={inventarioItems}
                  onChange={(v) => setForm({ ...form, receta: v })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-product-btn">
                {busy ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const price = Number(p.precio || 0);
          const disc = Number(p.descuento || 0);
          const finalPrice = price * (1 - disc / 100);
          const cover = (p.fotos && p.fotos[0]) || p.imagen;
          const nOpts = (p.opciones_color?.length || 0) + (p.opciones_estilo?.length || 0) + (p.opciones_tela?.length || 0) + (p.opciones_tamano?.length || 0);
          return (
            <Card key={p.id} className="border-zinc-200 rounded-md overflow-hidden hover:shadow-sm transition-transform duration-200 ease-out hover:-translate-y-px">
              <div className="aspect-[4/3] bg-zinc-100 flex items-center justify-center overflow-hidden">
                {cover ? (
                  <img src={cover} alt={p.nombre} className="w-full h-full object-cover" />
                ) : (
                  <Palette className="w-8 h-8 text-zinc-300" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold truncate">{p.categoria || "Sin categoría"}</div>
                    <div className="font-semibold truncate">{p.nombre}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {disc > 0 && <div className="text-[11px] text-zinc-400 line-through">{formatMoney(price)}</div>}
                    <div className="font-manrope font-black tracking-tight text-lg">{formatMoney(finalPrice)}</div>
                    {p.precio_costo > 0 && <div className="text-[10px] text-zinc-500">Costo {formatMoney(p.precio_costo)}</div>}
                  </div>
                </div>
                <div className="mt-3 text-xs text-zinc-500">
                  {nOpts} opciones de variante · {(p.fotos || []).length} fotos
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 gap-2" onClick={() => openEdit(p)}>
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => duplicate(p)} data-testid={`dup-product-${p.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Duplicar
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)} data-testid={`del-product-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-center text-zinc-400 text-sm py-12 border border-dashed rounded-md">
            Sin productos. Presiona &quot;Nuevo producto&quot;.
          </div>
        )}
      </div>
    </div>
  );
}
